<!--
  Công thức của dòng chi tiết AdHoc: "Điều kiện áp dụng" + "Khi sản phẩm đã có hoa hồng thường".
  Dùng chung cho modal thêm dòng, modal nhập Excel và modal áp công thức cho tất cả dòng,
  để ba chỗ luôn cùng một bộ ô nhập.
  v-model là object { amount1, amount1Operator, amount2, amount2Operator,
  conditionCategories, conditionLogic, rewardMode } — các trường khác của object được giữ nguyên.
-->
<template>
  <div>
    <div class="form-block">
      <div class="form-block__title">{{ conditionTitle }}</div>

      <label class="form-label">Doanh thu của dòng hàng</label>
      <!-- Một dải liền mạch đọc như bất phương trình: [số] [toán tử] DT [toán tử] [số] -->
      <div class="range-row mb-3">
        <b-form-input
          size="sm"
          class="range-row__amount"
          v-model="local.amount1"
          placeholder="Không giới hạn"
          autocomplete="off"
        ></b-form-input>
        <b-form-select
          size="sm"
          class="range-row__operator"
          v-model="local.amount1Operator"
          :options="operatorOptions"
        ></b-form-select>
        <span class="range-row__label">DT</span>
        <b-form-select
          size="sm"
          class="range-row__operator"
          v-model="local.amount2Operator"
          :options="operatorOptions"
        ></b-form-select>
        <b-form-input
          size="sm"
          class="range-row__amount"
          v-model="local.amount2"
          placeholder="Không giới hạn"
          autocomplete="off"
        ></b-form-input>
      </div>

      <!-- Đọc lại điều kiện bằng tiếng Việt để người nhập tự soát,
           tránh chọn nhầm chiều toán tử (vế trái là amount1 <op> DT). -->
      <div class="range-preview mb-3" :class="{ 'range-preview--warn': preview.warn }">
        <i
          class="mr-1"
          :class="preview.warn ? 'fas fa-exclamation-triangle' : 'fas fa-eye'"
        ></i>
        {{ preview.text }}
      </div>

      <b-row>
        <b-col md="8">
          <label class="form-label">Nhóm AdHoc kèm theo</label>
          <vue-multi-select
            v-model="local.conditionCategories"
            :options="adhocCategories"
            placeholder="Chọn nhóm điều kiện"
            option-label="name"
          ></vue-multi-select>
        </b-col>
        <b-col md="4">
          <label class="form-label">Cách ghép nhóm</label>
          <b-form-select
            size="sm"
            v-model="local.conditionLogic"
            :options="conditionLogicOptions"
            value-field="id"
            text-field="name"
            :disabled="local.conditionCategories.length < 2"
          ></b-form-select>
        </b-col>
      </b-row>
    </div>

    <div class="form-block" :class="{ 'form-block--last': last }">
      <div class="form-block__title">{{ rewardTitle }}</div>
      <b-row>
        <b-col md="6">
          <label class="form-label">Cách tính</label>
          <b-form-select
            size="sm"
            v-model="local.rewardMode"
            :options="rewardModeOptions"
            value-field="id"
            text-field="name"
          ></b-form-select>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import VueMultiSelect from 'vue-simple-multi-select';
import {
  COMMISSION_ADHOC_CONDITION_LOGIC,
  COMMISSION_ADHOC_CONDITION_LOGIC_NAME,
  COMMISSION_ADHOC_REWARD_MODE,
  COMMISSION_ADHOC_REWARD_MODE_NAME,
  COMMISSION_OPERATOR_OPTIONS,
  toOptionList,
} from './commission.constants';

const RULE_FIELDS = [
  'amount1',
  'amount1Operator',
  'amount2',
  'amount2Operator',
  'conditionCategories',
  'conditionLogic',
  'rewardMode',
];

/** Công thức mặc định: mọi mức doanh thu, không nhóm kèm theo (ghép VÀ), lấy số tiền lớn hơn */
export const emptyAdhocRule = () => ({
  amount1: '',
  amount1Operator: 1,
  amount2: '',
  amount2Operator: 1,
  conditionCategories: [],
  conditionLogic: COMMISSION_ADHOC_CONDITION_LOGIC.AND,
  rewardMode: COMMISSION_ADHOC_REWARD_MODE.MAX,
});

const pickRule = (source) => {
  const rule = emptyAdhocRule();
  RULE_FIELDS.forEach((field) => {
    if (source && source[field] !== undefined) {
      rule[field] = source[field];
    }
  });
  rule.conditionCategories = [...(rule.conditionCategories || [])];
  return rule;
};

const isSameRule = (left, right) =>
  RULE_FIELDS.every((field) =>
    field === 'conditionCategories'
      ? (left[field] || []).map((c) => c.id).join(',') ===
        (right[field] || []).map((c) => c.id).join(',')
      : left[field] === right[field],
  );

export default {
  components: { VueMultiSelect },
  props: {
    value: { type: Object, required: true },
    adhocCategories: { type: Array, default: () => [] },
    /** Hàm đọc điều kiện doanh thu thành câu — của màn cha, để bảng và ô nhập nói cùng một câu */
    describeRevenue: { type: Function, required: true },
    conditionTitle: { type: String, default: 'Điều kiện áp dụng' },
    rewardTitle: { type: String, default: 'Khi sản phẩm đã có hoa hồng thường' },
    /** true khi đây là khối cuối của modal — bỏ gạch phân cách dưới */
    last: { type: Boolean, default: false },
  },
  data() {
    return {
      local: pickRule(this.value),
      operatorOptions: COMMISSION_OPERATOR_OPTIONS,
      conditionLogicOptions: toOptionList(COMMISSION_ADHOC_CONDITION_LOGIC_NAME),
      rewardModeOptions: toOptionList(COMMISSION_ADHOC_REWARD_MODE_NAME),
    };
  },
  computed: {
    preview() {
      return this.describeRevenue(this.local);
    },
  },
  watch: {
    // Màn cha đặt lại form (mở modal mới) -> nạp lại ô nhập
    value: {
      deep: true,
      handler(next) {
        if (!isSameRule(next, this.local)) {
          this.local = pickRule(next);
        }
      },
    },
    local: {
      deep: true,
      handler(next) {
        if (!isSameRule(next, this.value)) {
          this.$emit('input', { ...this.value, ...pickRule(next) });
        }
      },
    },
  },
};
</script>

<style scoped>
.form-block {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebedf3;
}
.form-block--last {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: none;
}
.form-block__title {
  font-size: 13px;
  font-weight: 600;
  color: #181c32;
  margin-bottom: 12px;
}
.form-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}

/* Dải điều kiện doanh thu — đọc liền như một bất phương trình */
.range-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.range-row__amount {
  flex: 1 1 0;
  min-width: 0;
  text-align: right;
}
.range-row__operator {
  flex: 0 0 88px;
  width: 88px;
}
.range-row__label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #181c32;
  padding: 0 4px;
}

/* Dòng đọc lại điều kiện — xanh khi hợp lệ, vàng khi cấu hình vô nghĩa */
.range-preview {
  font-size: 13px;
  font-weight: 500;
  color: #187de4;
  background: #f1faff;
  border: 1px solid #cfe7fb;
  border-radius: 4px;
  padding: 8px 12px;
}
.range-preview--warn {
  color: #b8860b;
  background: #fff8e6;
  border-color: #ffe2a8;
}

/* Màn hẹp: dải điều kiện xuống dòng thay vì bị bóp méo */
@media (max-width: 575.98px) {
  .range-row {
    flex-wrap: wrap;
  }
  .range-row__amount {
    flex: 1 1 40%;
  }
  .range-row__label {
    width: 100%;
    text-align: center;
    padding: 4px 0;
  }
}
</style>
